// Affiliate attribution shim.
//
// Billing lives in Chargebee, and Chargebee is the only correct place to hook
// commission tracking: Stripe sits underneath it as a gateway and never sees
// plan IDs or coupon redemptions, and PayPal-settled subscriptions never touch
// Stripe at all. The *sale* side is therefore entirely a Chargebee webhook the
// affiliate vendor configures server-side — none of it lives in this file.
//
// What the client still has to do is bridge the gap between the referral click
// and the Chargebee customer, because those are usually days apart:
//
//   /signup/:ref  →  Cognito sign-up  →  ...browse...  →  Chargebee checkout
//   ^ captureRef()   ^ registerLead()                     ^ vendor webhook
//
// The webhook matches the Chargebee customer back to the referral *by email*,
// so without registerLead() the vendor has no email on file and only
// same-session conversions ever get attributed — a minority for a considered
// purchase like a subscription.
//
// This module is INERT until VENDOR is set: it records the ref locally and
// no-ops every vendor call, so it is safe to ship before a platform is chosen.

// ---------------------------------------------------------------------------
// Vendor configuration — the only block that changes when a platform is picked
// ---------------------------------------------------------------------------
// null | 'firstpromoter' | 'tolt'
// Live as of the FirstPromoter setup (cid sibe2ykm, script tag in index.html).
const VENDOR = 'firstpromoter'

// Attribution is moving from the browser to the backend: `refSlug` is persisted
// on the user record at account creation, the users-table stream enqueues it,
// and an SQS consumer registers the lead against FirstPromoter's track API.
//
// TRUE as of the prod cutover: the users-table stream registers the lead
// server-side against FirstPromoter's track API (proven end-to-end — it's the
// path that landed the first commission). registerLead() below now no-ops, so
// the redundant client-side fpr('referral') call no longer fires.
const SERVER_SIDE_ATTRIBUTION = true

const VENDORS = {
    // fpr.js reads ?fpr= off the landing URL and drops its own _fprom_* cookies.
    // Signup call is fpr("referral", {email, uid}) — either field alone suffices.
    // Safe to call unconditionally: it no-ops when there is no referral cookie.
    firstpromoter: {
        param: 'fpr',
        ready: () => typeof window.fpr === 'function',
        signup: ({ email, uid }) => window.fpr('referral', { email, uid })
    },
    // tolt.js sets window.tolt_referral once a click is attributed, and its
    // signup() takes the email only. Their docs are explicit that it must not
    // be called when tolt_referral is absent, hence the stricter ready().
    // NB: confirm `param` against the link format Tolt actually issues you.
    tolt: {
        param: 'ref',
        ready: () => !!(window.tolt && window.tolt_referral),
        signup: ({ email }) => window.tolt.signup(email)
    }
}

const vendor = () => (VENDOR && VENDORS[VENDOR]) || null

// ---------------------------------------------------------------------------

const REF_KEY = 'genoAffiliateRef'
const LEAD_KEY = 'genoAffiliateLead'
// Must match "Cookie life (days)" in the FirstPromoter campaign settings —
// currently 90. If ours expires first, a visitor converting on day 75 still has
// a valid FirstPromoter cookie while our slug has gone, so the server-side path
// registers nothing and the commission is lost. Update both together.
const REF_TTL_MS = 90 * 24 * 60 * 60 * 1000

function read(key) {
    try {
        return JSON.parse(localStorage.getItem(key))
    } catch (err) {
        return null
    }
}

function write(key, value) {
    try {
        localStorage.setItem(key, JSON.stringify(value))
    } catch (err) {
        // Safari private mode throws on setItem — attribution degrades to
        // whatever the vendor's own cookie holds, which is the common path
        // anyway. Never let this break sign-in.
    }
}

// The one definition of a usable ref. Refs arrive straight off the URL, so this
// is the character class Chargebee allows in coupon codes (letters and digits)
// plus - and _ — meaning a ref can double as a coupon code with no second
// mapping table, and survives being read aloud in a podcast.
const SLUG_RE = /^[A-Za-z0-9_-]+$/
const SLUG_MAX = 64

function sanitize(raw) {
    if (typeof raw !== 'string') return null
    const ref = raw.trim().slice(0, SLUG_MAX)
    return SLUG_RE.test(ref) ? ref : null
}

// Can this alias be handed out as a referral slug?
//
// The server-side alias validator is a BLOCKLIST (a profanity filter with some
// punctuation bolted on), so it cannot guarantee URL safety: `/` is not on the
// list and would stop /signup/:ref matching at all, and non-ASCII is unfiltered
// and gets silently dropped by sanitize() above. Rather than hand someone a
// link that quietly never attributes, callers use this to show nothing.
//
// The alias lambda now enforces this positively on write, so every NEW or
// changed alias is slug-safe by construction. This guard still stays: aliases
// stored before that change were never re-validated, and a user who never edits
// their nickname keeps an unsafe one indefinitely. Only safe to delete once the
// existing nickname values have been audited and migrated.
export function isSlugSafe(alias) {
    return typeof alias === 'string' && alias.length <= SLUG_MAX && SLUG_RE.test(alias)
}

// The shareable referral URL for an alias, or null when the alias cannot be
// expressed as one.
//
// Lowercased deliberately. FirstPromoter's `ref_id` is documented as lowercase
// letters, digits, hyphens and underscores only, so the promoter we create for
// an alias is registered lowercase; emitting the link in the same case means it
// matches `ref_id` exactly rather than relying on their tracker being
// case-insensitive, which isn't documented either way. No identity is lost —
// alias uniqueness is already enforced on a lowercased S3 key, so `Razib` and
// `razib` are the same user.
export function referralSlug(alias) {
    return isSlugSafe(alias) ? alias.toLowerCase() : null
}

export function referralLink(alias, origin) {
    const slug = referralSlug(alias)
    if (!slug) return null
    const base = origin || (typeof window !== 'undefined' ? window.location.origin : '')
    return `${base}/signup/${slug}`
}

// The id a username maps to as a Chargebee customer. Chargebee rejects ids
// shorter than four characters, hence the padding — this mirrors the transform
// the subscription checkout applies in Nav, and exists as a shared function so
// the two cannot drift.
//
// It matters here because this value is the join key between a referral lead
// and the Chargebee customer that eventually converts. Email is not usable for
// that: users can change their address between signing up and subscribing, and
// the conversion would then silently attribute to nobody.
export function chargebeeCustomerId(username) {
    if (typeof username !== 'string' || !username) return ''
    return username.length <= 3 ? ('~~~' + username).slice(-4) : username
}

// Persist the ref from a /signup/:ref landing. Returns the cleaned ref, or
// null if it was malformed.
// Lowercased on capture. FirstPromoter's ref_id charset is lowercase-only, so
// a legitimate ref can never contain uppercase — which means someone typing
// /signup/Advanced from a podcast or a capitalised link would otherwise send
// ?fpr=Advanced and match nothing, silently. Revisit if VENDOR changes: this is
// safe precisely because of FirstPromoter's charset rule, not in general.
export function captureRef(raw) {
    const ref = sanitize(raw)
    if (!ref) return null
    const normalized = ref.toLowerCase()
    write(REF_KEY, { ref: normalized, ts: Date.now() })
    return normalized
}

// The ref this visitor landed with, or null once past the attribution window.
// This is *our* copy — the vendor keeps its own cookie and remains the source
// of truth for commission. Use this for UI concerns (e.g. pre-applying the
// influencer's Chargebee coupon at checkout), not for attribution.
export function getRef() {
    const record = getRefRecord()
    return record ? record.ref : null
}

// The captured ref together with WHEN it was captured.
//
// The timestamp is what makes retroactive attribution impossible: an account is
// only credited to a referral if the account was created *after* the click. An
// existing user who wanders through /signup/someone stays unattributed, which
// matters because the alternative is affiliates earning commission on customers
// you already had.
export function getRefRecord() {
    const stored = read(REF_KEY)
    if (!stored || !stored.ref) return null
    if (Date.now() - (stored.ts || 0) > REF_TTL_MS) {
        clearRef()
        return null
    }
    return stored
}

export function clearRef() {
    try {
        localStorage.removeItem(REF_KEY)
    } catch (err) {
        /* no-op */
    }
}

// Query param the chosen vendor's script reads off the landing URL. Falls back
// to a neutral `ref` while VENDOR is null so the value still shows up in the
// URL and in analytics during testing.
export function vendorParam() {
    const v = vendor()
    return v ? v.param : 'ref'
}

// Register a newly signed-up user as a lead with the vendor, so the Chargebee
// webhook can match them by email whenever they eventually subscribe.
//
// Idempotent per user: safe to call on every sign-in, which is what makes it
// provider-agnostic — it lands on the first sign-in regardless of whether the
// account came from Cognito email/password, Google, Facebook or Apple.
//
// Deliberately NOT gated on getRef(): the plain affiliate link (?fpr=abc) never
// passes through our /signup/:ref route, so our localStorage copy is often
// absent on a perfectly valid referral. Each vendor's ready() decides instead.
export function registerLead({ email, uid }) {
    // Superseded by the server-side path once that is live — see
    // SERVER_SIDE_ATTRIBUTION above.
    if (SERVER_SIDE_ATTRIBUTION) return false
    const v = vendor()
    if (!v || (!email && !uid)) return false
    const id = uid || email
    const seen = read(LEAD_KEY)
    if (seen && seen.id === id) return false
    if (!v.ready()) return false
    try {
        v.signup({ email: email, uid: uid })
        write(LEAD_KEY, { id: id, ts: Date.now() })
        return true
    } catch (err) {
        return false
    }
}
